import cookies from 'js-cookie'
import { OpenFeature, InMemoryProvider } from '@openfeature/web-sdk'
import { DatadogProvider } from '@datadog/openfeature-browser'
import { parseJwtPayload } from '@/utils/parseJwt'

export default {
  async install(app) {
    const isLocal = import.meta.env.VITE_ENV === 'local'
    const devEnv = import.meta.env.VITE_DEV_ENV || 'scripps'

    if (!isLocal && devEnv === 'scripps') {
      const provider = new DatadogProvider({
        applicationId: import.meta.env.VITE_DATADOG_APP_ID,
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        env: import.meta.env.VITE_ENV,
        enableExposureLogging: true,
        enableFlagEvaluationTracking: true
      })
      await OpenFeature.setProviderAndWait(provider)

      // Set targeting context from JWT cookie (mirrors datadogRum.setUser in _globals.js)
      const _cookie = cookies.get('EWS_AUTH_KEY') ?? ''
      if (_cookie && _cookie.length > 20) {
        const { profile } = parseJwtPayload(_cookie) ?? {}
        await OpenFeature.setContext({
          targetingKey: profile?.empid?.toString() || 'unknown',
          name: `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim(),
          email: profile?.email_address || '',
          station: cookies.get('currentStation') || localStorage.getItem('ews_current_station') || ''
        })
      }
    } else {
      // Local/non-scripps: use in-memory provider with empty flag set — returns defaults safely.
      // Synthetic delay + setContext mirror prod's Datadog fetch + setContext sequence so local
      // dev surfaces timing bugs (e.g. flags evaluated before ContextChanged) instead of masking them.
      const SYNTHETIC_INIT_DELAY_MS = 300
      const SYNTHETIC_CONTEXT_DELAY_MS = 150
      const provider = new InMemoryProvider({
        'use-production-elements': {
          defaultVariant: 'on',
          variants: {
            on: true,
            off: false
          }
        },
        'use-story-ai': {
          defaultVariant: 'on',
          variants: {
            on: true,
            off: false
          }
        },
        'show-production-menu': {
          defaultVariant: 'on',
          variants: {
            on: true,
            off: false
          }
        },
        'some-string-flag': {
          defaultVariant: 'v2',
          variants: {
            v1: 'old',
            v2: 'new'
          }
        },
        'some-number-flag': {
          defaultVariant: 'high',
          variants: {
            low: 10,
            high: 100
          }
        },
        'use-video-ai-tabs': {
          defaultVariant: 'on',
          variants: {
            on: true,
            off: false
          }
        }
      })
      provider.initialize = () => new Promise((resolve) => setTimeout(resolve, SYNTHETIC_INIT_DELAY_MS))
      await OpenFeature.setProviderAndWait(provider)

      await new Promise((resolve) => setTimeout(resolve, SYNTHETIC_CONTEXT_DELAY_MS))
      await OpenFeature.setContext({
        targetingKey: 'local-dev',
        name: 'Local Dev',
        email: 'local@dev',
        station: localStorage.getItem('ews_current_station') || ''
      })
    }

    app.config.globalProperties.$featureFlags = OpenFeature.getClient()
  }
}
